import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChildren, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, QueryList, SimpleChanges, forwardRef } from "@angular/core";
import { Subscription } from "rxjs";
import { CsSegmentButtonBase } from "./cs-segment-button-interface";

@Component({
    selector: 'cs-segment',
    templateUrl: 'cs-segment.html',
    host: { 'class': 'segment' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CsSegmentComponent implements AfterContentInit, OnDestroy {
  @Input() selection: any;

  // Switches the whole group off: it fades out, stops taking pointer events, drops out of
  // the tab order, and its buttons refuse to select. The buttons read this flag themselves
  // in onClick, so a keyboard press or a direct call is refused too -- `pointer-events:
  // none` alone would only stop the mouse and the finger.
  @Input() disabled = false;

  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();

  @HostBinding('class.segment-disabled') get isDisabled(): boolean {
    return this.disabled;
  }

  // null rather than false: an aria-disabled="false" attribute is noise in the
  // accessibility tree, and returning null leaves the attribute off entirely.
  @HostBinding('attr.aria-disabled') get ariaDisabled(): true | null {
    return this.disabled ? true : null;
  }

  @ContentChildren(forwardRef(() => CsSegmentButtonBase), { descendants: true }) buttonsComponents: QueryList<CsSegmentButtonBase>;

  buttons: CsSegmentButtonBase[];
  private buttonChanges?: Subscription;

  constructor(public elementRef: ElementRef) {
  }

  private refreshSelection() {
    if (this.buttons) {
      for (let button of this.buttons) {
        if (button.getValue() === this.selection) {
          button.select();
        } else {
          button.deselect();
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selection) {
      this.refreshSelection();
    }
  }

  ngAfterContentInit() {
    this.takeButtonSnapshot();

    // The set of buttons can change while the group itself stays alive -- the
    // portion screen's scope dial gains and loses positions as the data allows.
    // Without this the list would stay at its first snapshot for ever: a button
    // added later is never highlighted and never deselected when another is
    // picked, so the dial can end up showing two active segments, or none.
    this.buttonChanges = this.buttonsComponents.changes.subscribe(() => this.takeButtonSnapshot());
  }

  ngOnDestroy() {
    this.buttonChanges?.unsubscribe();
  }

  private takeButtonSnapshot() {
    this.buttons = this.buttonsComponents.toArray();
    this.refreshSelection();
  }

  select(button: CsSegmentButtonBase) {
    // a disabled group never changes its selection, whatever asked it to
    if (this.disabled) {
      return;
    }

    this.selection = button.getValue();
    this.selectionChange.emit(this.selection);

    this.buttons.forEach((b) => {
      b.deselect();
    });
  }
}
